import React, { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import api from "../../api";
import { Location } from "../../types";
interface UpdateRouteDto {
  name: string | undefined;
  startLoc: string | undefined;
  endLoc: string | undefined;
}

interface UpdateSettingsModalProps {
  routeId: number;
  isOpen: boolean;
  onClose: () => void;
  onUpdated: (updatedRoute: any) => void;
}

const UpdateSettingsModal: React.FC<UpdateSettingsModalProps> = ({
  routeId,
  isOpen,
  onClose,
  onUpdated,
}) => {
  const [name, setName] = useState<string>("");
  const [startLoc, setStartLoc] = useState<string>(""); // store location id as string
  const [endLoc, setEndLoc] = useState<string>("");
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  // Fetch locations when the modal mounts.
  useEffect(() => {
    const fetchAllLocations = async () => {
      try {
        const { data: response } = await api.get("/route/fetch-locations");
        setLocations(response);
      } catch (error) {
        console.error("Failed to fetch locations:", error);
      }
    };

    fetchAllLocations();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload: UpdateRouteDto = {
      name,
      startLoc,
      endLoc,
    };

    try {
      setLoading(true);
      await api.patch(`settings/${routeId}`, payload);
      const { data: updatedRoute } = await api.get(`/settings/${routeId}`);
      toast.success("Route Updated");
      onUpdated(updatedRoute);
      onClose();
    } catch (error) {
      toast.error("Error updating route");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null; // Hide modal if not open

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white p-8 rounded-lg w-full max-w-lg">
        <h2 className="text-xl font-bold mb-4">Update Route</h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="name" className="block font-medium">
              Name:
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="mt-2 block w-full p-2 border border-gray-300 rounded"
            />
          </div>
          <div className="mb-4">
            <label htmlFor="startLoc" className="block font-medium">
              Start Location:
            </label>
            <select
              id="startLoc"
              value={startLoc}
              onChange={(e) => setStartLoc(e.target.value)}
              required
              className="mt-2 block w-full p-2 border border-gray-300 rounded"
            >
              <option value="">Select a start location</option>
              {locations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.city} {location.state && `, ${location.state}`}
                </option>
              ))}
            </select>
          </div>
          <div className="mb-4">
            <label htmlFor="endLoc" className="block font-medium">
              End Location:
            </label>
            <select
              id="endLoc"
              value={endLoc}
              onChange={(e) => setEndLoc(e.target.value)}
              required
              className="mt-2 block w-full p-2 border border-gray-300 rounded"
            >
              <option value="">Select an end location</option>
              {locations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.city} {location.state && `, ${location.state}`}
                </option>
              ))}
            </select>
          </div>
          <div className="flex gap-4">
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 rounded bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50"
            >
              {loading ? "Updating..." : "Update Route"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded bg-gray-300 text-black hover:bg-gray-400"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdateSettingsModal;
